import * as Sentry from '@sentry/react';
import axios, { AxiosRequestConfig } from 'axios';

import { storage } from '@utils/browser';
import { isRequestError } from '@utils/requests';

import { config } from 'app-config';

// Check if there's a token in localStorage. (For debugging)
let debugToken = storage.get('dbg');

const apiRequest = axios.create({
  baseURL: config.API_URL,
  xsrfHeaderName: 'X-CSRFToken',
  xsrfCookieName: 'csrftoken',
});

function addResponseErrorInterceptor(onRejected: (response: any) => any): number {
  return apiRequest.interceptors.response.use(undefined, onRejected);
}

function removeResponseInterceptor(interceptor: number) {
  apiRequest.interceptors.response.eject(interceptor);
}

interface RequestErrorReturn {
  error: true;
  status?: number;
  data?: string;
}

interface RequestSuccessReturn<T> {
  data: T;
  error: false;
  status: number;
}

async function getCSRFToken(): Promise<string> {
  const response = await unhandledRequest.get<void>({
    url: '/auth/csrf/',
  });

  return response.headers?.['x-csrftoken'] ?? '';
}

async function doUnhandledRequest<T>(config: AxiosRequestConfig) {
  let headers: Partial<AxiosRequestConfig['headers']> = {
    'Accept-Language': storage.get('50_lang') || 'en',
    ...config.headers,
  };

  const isAwsRequest = config.url?.includes('amazonaws.com');

  if (isAwsRequest) {
    // We don't want to set credentials: include for AWS blob requests
    // as it will throw CORS errors
    config.withCredentials = false;
  } else if (debugToken) {
    headers['Authorization'] = 'Token ' + debugToken;
    config.withCredentials = false;
  } else {
    config.withCredentials = true;
    if (config.method?.toLowerCase() !== 'get') {
      headers['X-CSRFToken'] = await getCSRFToken();
    }
  }

  return apiRequest.request<T>({
    ...config,
    headers,
    ...(config.responseType === 'blob' && {
      // Have to unset the 'application/json' content type
      // when we fetch blobs from s3.
      transformRequest: [
        (data, headers) => {
          headers.common?.delete('Content-Type');
          return data;
        },
      ],
    }),
  });
}

async function doRequest<T>(
  config: AxiosRequestConfig = {},
): Promise<RequestErrorReturn | RequestSuccessReturn<T>> {
  try {
    const response = await doUnhandledRequest<T>(config);

    return {
      data: response.data,
      status: response.status,
      error: false,
    };
  } catch (error) {
    let errorRet: RequestErrorReturn = {
      error: true,
    };

    if (isRequestError(error)) {
      errorRet.data = error.response?.data || error.response?.statusText || 'Error';
      errorRet.status = error.response?.status;
    }

    reportRequestError(error, { ...errorRet });

    return errorRet;
  }
}

/** For react-query callers: `unhandledRequest` rethrows without reporting. */
export function reportRequestError(error: unknown, extra?: Record<string, unknown>) {
  if (process.env.NODE_ENV !== 'production') return;

  const status = isRequestError(error) ? error.response?.status : undefined;
  if (status === 404 || status === 401) return;

  Sentry.captureException(error, { extra: extra ?? { status } });
}

/**
 * Basic handled request that handles the axios exceptions (401, 404, 500, etc)
 */
export const request = {
  get: async function get<T>(config: AxiosRequestConfig) {
    return doRequest<T>({ ...config, method: 'get' });
  },
  post: async function post<T>(config: AxiosRequestConfig) {
    return doRequest<T>({ ...config, method: 'post' });
  },
  put: async function put<T>(config: AxiosRequestConfig) {
    return doRequest<T>({ ...config, method: 'put' });
  },
  patch: async function patch<T>(config: AxiosRequestConfig) {
    return doRequest<T>({ ...config, method: 'patch' });
  },
  delete: async function del<T>(config: AxiosRequestConfig) {
    return doRequest<T>({ ...config, method: 'delete' });
  },
  addResponseErrorInterceptor,
  removeResponseInterceptor,
  setDebugToken,
  removeDebugToken,
};

/**
 * Basic request that doesn't handle exceptions. This is used in
 * react-query hooks because react-query has a better flow for
 * errors than using the handled request above.
 *
 * Ideally, this will completely replace the handled request later.
 */
export const unhandledRequest = {
  get: async function get<T>(config: AxiosRequestConfig) {
    return doUnhandledRequest<T>({ ...config, method: 'get' });
  },
  post: async function post<T>(config: AxiosRequestConfig) {
    return doUnhandledRequest<T>({ ...config, method: 'post' });
  },
  put: async function put<T>(config: AxiosRequestConfig) {
    return doUnhandledRequest<T>({ ...config, method: 'put' });
  },
  patch: async function patch<T>(config: AxiosRequestConfig) {
    return doUnhandledRequest<T>({ ...config, method: 'patch' });
  },
  delete: async function del<T>(config: AxiosRequestConfig) {
    return doUnhandledRequest<T>({ ...config, method: 'delete' });
  },
  addResponseErrorInterceptor,
  removeResponseInterceptor,
  setDebugToken,
  removeDebugToken,
};

function setDebugToken(token: string) {
  storage.set('dbg', token);
}

function removeDebugToken() {
  storage.remove('dbg');
  debugToken = null;
}

// @ts-ignore
window.setDebugToken = setDebugToken;

// @ts-ignore
window.removeDebugToken = removeDebugToken;
